import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  DatePicker,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../../i18n';
import EChart from '../../components/charts/EChart';
import { useListFieldsQuery, useLazyListPlotsQuery } from '../../api/fieldsApi';
import { useLazyGetPlotPlanQuery, usePatchPlotPlanMutation } from '../../api/planApi';
import {
  useCreateGreenhouseClimatePeriodMutation,
  useCreateSimEventMutation,
  useDeleteSimEventMutation,
  useDeleteGreenhouseClimatePeriodMutation,
  useGetGreenhouseClimateStatusQuery,
  useGetGreenhouseClimateTimeseriesQuery,
  useListSimEventsQuery,
  useListGreenhouseClimatePeriodsQuery,
  useUpdateGreenhouseClimatePeriodMutation,
} from '../../api/simulationApi';
import type { StructuralObject } from '../../types/plan';
import { disableBeforeOpenDay, isBeforeOpenDay } from '../../utils/openDayDate';
import type {
  GreenhouseHeatingSnapshot,
  GreenhouseClimatePeriod,
  SimEvent,
  UpsertGreenhouseClimatePeriodPayload,
} from '../../types/simulation';

const { Text } = Typography;

/** Текст панели вне React-рендера (колбэки, хелперы): simulation:greenhouse.<key>. */
const gt = (key: string, options?: Record<string, unknown>): string =>
  i18n.t(`simulation:greenhouse.${key}`, options ?? {}) as string;

// Подписи подтипов: simulation:greenhouse.subtype.<subtype>
const GREENHOUSE_SUBTYPES = new Set(['greenhouse', 'parnik']);

interface GreenhouseTarget {
  id: string;
  label: string;
  subtype: string;
  fieldId: string;
  fieldName: string;
  plotId: string;
  plotName: string;
  cellCount: number;
  activeFrom: string | null;
  activeTo: string | null;
}

interface GreenhouseClimatePanelProps {
  farmId: string;
  sessionId: string;
  saveId: string | null;
  simDate: string;
}

interface ClimatePeriodFormValues {
  startDate: Dayjs;
  endDate?: Dayjs | null;
  internalTempC?: number | null;
  internalTempAmplitudeC?: number | null;
  internalHumidityPct?: number | null;
  ventilationEnabled?: boolean;
  ventilationMaxC?: number | null;
  allowExternalPrecipitation: boolean;
  isIsolatedZone: boolean;
}

type HeatingEventAction = 'heating_on' | 'heating_off';

interface HeatingEventFormValues {
  eventDate: Dayjs;
  action: HeatingEventAction;
  mode: 'manual' | 'follow_tech_card';
  minC?: number | null;
  maxC?: number | null;
  ventilationMaxC?: number | null;
}

type SimEventLike = SimEvent & Record<string, unknown>;

const HEATING_MODES = new Set(['manual', 'follow_tech_card']);
const heatingModeLabel = (mode: string) => (HEATING_MODES.has(mode) ? gt(`heatingMode.${mode}`) : mode);

const HEATING_ACTIONS = new Set<string>(['heating_on', 'heating_off']);
const heatingActionLabel = (action: string) => (HEATING_ACTIONS.has(action) ? gt(`heatingAction.${action}`) : action);

const greenhouseSubtypeLabel = (subtype?: string | null) => {
  if (!subtype) return gt('subtype.greenhouse');
  return GREENHOUSE_SUBTYPES.has(subtype) ? gt(`subtype.${subtype}`) : subtype;
};

const isGreenhouseObject = (structuralObject: StructuralObject) => (
  structuralObject.kind === 'covering' && ['greenhouse', 'parnik'].includes(structuralObject.subtype)
);

const formatOptionalNumber = (value: number | null | undefined, suffix = '') => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const normalized = Number(value);
  return `${normalized % 1 === 0 ? normalized.toFixed(0) : normalized.toFixed(1)}${suffix}`;
};

const formatTempBand = (temp: number | null | undefined, amplitude: number | null | undefined) => {
  if (temp == null) return '—';
  if (amplitude == null) return `${formatOptionalNumber(temp, '°C')}`;
  return `${formatOptionalNumber(temp, '°C')} ± ${formatOptionalNumber(amplitude, '°C')}`;
};

const buildClimatePayload = (
  values: ClimatePeriodFormValues,
  structuralObjectId: string,
): UpsertGreenhouseClimatePeriodPayload => ({
  structuralObjectId,
  startDate: values.startDate.format('YYYY-MM-DD'),
  endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
  internalTempC: values.internalTempC ?? null,
  internalTempAmplitudeC: values.internalTempAmplitudeC ?? null,
  internalHumidityPct: values.internalHumidityPct ?? null,
  ventilationEnabled: values.ventilationEnabled === true,
  ventilationMaxC: values.ventilationEnabled === true ? (values.ventilationMaxC ?? 35) : null,
  allowExternalPrecipitation: values.allowExternalPrecipitation,
  isIsolatedZone: values.isIsolatedZone,
});

const extractApiError = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const maybeAny = error as any;
    const text = maybeAny?.data?.message || maybeAny?.error || fallback;
    const suggestion = maybeAny?.data?.suggestion;
    return typeof suggestion === 'string' && suggestion ? `${text} ${suggestion}` : text;
  }
  return fallback;
};

const getHeatingSnapshot = (
  heating: GreenhouseHeatingSnapshot | null | undefined,
): GreenhouseHeatingSnapshot | null => {
  if (!heating || typeof heating !== 'object') return null;
  return heating;
};

const parseHeatingEventNotes = (event: SimEventLike): Record<string, unknown> | null => {
  const notes = event.notes;
  if (!notes) return null;
  if (typeof notes === 'object') {
    return notes as Record<string, unknown>;
  }
  if (typeof notes !== 'string') return null;
  try {
    const parsed = JSON.parse(notes);
    return typeof parsed === 'object' && parsed !== null
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

const getSimEventAction = (event: SimEventLike): string => {
  const raw = event.action ?? event.type;
  return typeof raw === 'string' ? raw.toLowerCase() : '';
};

const getSimEventStructuralObjectId = (event: SimEventLike): string => {
  const raw = event.structuralObjectId ?? event.structural_object_id;
  return raw == null ? '' : String(raw);
};

const buildHeatingSummary = (heating: GreenhouseHeatingSnapshot | null | undefined): string => {
  const snapshot = getHeatingSnapshot(heating);
  if (!snapshot) return gt('heatingSummary.inactive');
  if (snapshot.mode === 'follow_tech_card') return gt('heatingSummary.followTechCard');

  const parts: string[] = [];
  if (snapshot.minC != null) parts.push(`min ${formatOptionalNumber(snapshot.minC, '°C')}`);
  if (snapshot.maxC != null) parts.push(`max ${formatOptionalNumber(snapshot.maxC, '°C')}`);
  if (snapshot.ventilationMaxC != null) parts.push(gt('ventShort', { value: formatOptionalNumber(snapshot.ventilationMaxC, '°C') }));
  return parts.length > 0 ? parts.join(' · ') : gt('heatingMode.manual');
};

const buildHeatingFormDefaults = (
  currentDate: string,
  heating: GreenhouseHeatingSnapshot | null | undefined,
): Partial<HeatingEventFormValues> => {
  const snapshot = getHeatingSnapshot(heating);
  return {
    eventDate: dayjs(currentDate),
    action: 'heating_on',
    mode: snapshot?.mode === 'follow_tech_card' ? 'follow_tech_card' : 'manual',
    minC: snapshot?.minC ?? 5,
    maxC: snapshot?.maxC ?? 25,
    ventilationMaxC: snapshot?.ventilationMaxC ?? 35,
  };
};

const GreenhouseClimatePanel: React.FC<GreenhouseClimatePanelProps> = ({ farmId, sessionId, saveId, simDate }) => {
  const { t } = useTranslation('simulation');
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm<ClimatePeriodFormValues>();
  const [heatingForm] = Form.useForm<HeatingEventFormValues>();
  // Открытый день D+1: дни ≤ курсора посчитаны, события в них не создаются и не правятся.
  const openDay = simDate ? dayjs(simDate).add(1, 'day').format('YYYY-MM-DD') : null;

  const { data: fields, isFetching: loadingFields } = useListFieldsQuery(farmId, {
    skip: !farmId,
  });
  const [triggerListPlots] = useLazyListPlotsQuery();
  const [triggerGetPlotPlan] = useLazyGetPlotPlanQuery();

  const [targets, setTargets] = useState<GreenhouseTarget[]>([]);
  const [discoveringTargets, setDiscoveringTargets] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<GreenhouseClimatePeriod | null>(null);

  const [createPeriod, { isLoading: creatingPeriod }] = useCreateGreenhouseClimatePeriodMutation();
  const [updatePeriod, { isLoading: updatingPeriod }] = useUpdateGreenhouseClimatePeriodMutation();
  const [deletePeriod, { isLoading: deletingPeriod }] = useDeleteGreenhouseClimatePeriodMutation();
  const [patchPlotPlan, { isLoading: patchingWindow }] = usePatchPlotPlanMutation();
  const [createSimEvent, { isLoading: creatingHeatingEvent }] = useCreateSimEventMutation();
  const [deleteSimEvent, { isLoading: deletingHeatingEvent }] = useDeleteSimEventMutation();

  const fieldIdsKey = useMemo(
    () => (fields || []).map((field) => field.id).sort().join('|'),
    [fields],
  );

  useEffect(() => {
    if (!fields?.length) {
      setTargets([]);
      return;
    }

    let cancelled = false;
    const loadTargets = async () => {
      setDiscoveringTargets(true);
      try {
        const plotsByField = await Promise.all(
          fields.map(async (field) => {
            const plots = await triggerListPlots(field.id, true).unwrap().catch(() => []);
            return { field, plots };
          }),
        );

        const plansByPlot = await Promise.all(
          plotsByField.flatMap(({ field, plots }) =>
            plots.map(async (plot) => {
              const plan = saveId
                ? await triggerGetPlotPlan({ saveId, plotId: plot.id, fieldId: field.id }, true)
                .unwrap()
                .catch(() => null)
                : null;
              return { field, plot, plan };
            }),
          ),
        );

        const nextTargets = plansByPlot
          .flatMap(({ field, plot, plan }) => {
            if (!plan?.structuralObjects?.length) return [];
            return plan.structuralObjects
              .filter(isGreenhouseObject)
              .map((structuralObject) => {
                const cellCount = (plan.cells || []).filter(
                  (cell) => cell.structuralObjectId === structuralObject.id,
                ).length;
                const baseLabel = structuralObject.name?.trim() || greenhouseSubtypeLabel(structuralObject.subtype);
                return {
                  id: structuralObject.id,
                  label: `${baseLabel} · ${field.name} / ${plot.name}`,
                  subtype: structuralObject.subtype,
                  fieldId: field.id,
                  fieldName: field.name,
                  plotId: plot.id,
                  plotName: plot.name,
                  cellCount,
                  activeFrom: structuralObject.activeFrom ?? null,
                  activeTo: structuralObject.activeTo ?? null,
                } satisfies GreenhouseTarget;
              });
          })
          .sort((left, right) => left.label.localeCompare(right.label, currentLocale()));

        if (!cancelled) {
          setTargets(nextTargets);
        }
      } finally {
        if (!cancelled) {
          setDiscoveringTargets(false);
        }
      }
    };

    loadTargets();
    return () => {
      cancelled = true;
    };
  }, [farmId, fieldIdsKey, fields, saveId, triggerGetPlotPlan, triggerListPlots]);

  useEffect(() => {
    if (!targets.length) {
      setSelectedTargetId('');
      return;
    }
    if (!targets.some((target) => target.id === selectedTargetId)) {
      setSelectedTargetId(targets[0].id);
    }
  }, [selectedTargetId, targets]);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.id === selectedTargetId) || null,
    [selectedTargetId, targets],
  );

  const { data: periodsData, isFetching: loadingPeriods } = useListGreenhouseClimatePeriodsQuery(
    { sessionId, structuralObjectId: selectedTargetId },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: climateStatus, isFetching: loadingStatus } = useGetGreenhouseClimateStatusQuery(
    { sessionId, structuralObjectId: selectedTargetId, date: simDate },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: climateTimeseries, isFetching: loadingTimeseries } = useGetGreenhouseClimateTimeseriesQuery(
    { sessionId, structuralObjectId: selectedTargetId },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: heatingEventsData, isFetching: loadingHeatingEvents } = useListSimEventsQuery(
    { saveId: saveId || '', sessionId, structuralObjectId: selectedTargetId },
    { skip: !saveId || !selectedTargetId },
  );

  const periods = periodsData?.periods || [];
  const busyMutation = creatingPeriod || updatingPeriod || deletingPeriod;
  const currentHeating = useMemo(
    () => getHeatingSnapshot(climateStatus?.layers.heating || climateStatus?.heating),
    [climateStatus?.heating, climateStatus?.layers.heating],
  );
  const heatingAction = Form.useWatch('action', heatingForm);
  const heatingMode = Form.useWatch('mode', heatingForm);
  const heatingEvents = useMemo(
    () => ((heatingEventsData?.events || []) as SimEventLike[])
      .filter((event) => ['heating_on', 'heating_off'].includes(getSimEventAction(event)))
      .filter((event) => !selectedTargetId || getSimEventStructuralObjectId(event) === selectedTargetId)
      .slice()
      .sort((left, right) => String(right.date || '').localeCompare(String(left.date || ''))),
    [heatingEventsData?.events, selectedTargetId],
  );

  useEffect(() => {
    heatingForm.setFieldsValue(buildHeatingFormDefaults(dayjs(simDate).add(1, 'day').format('YYYY-MM-DD'), currentHeating));
  }, [currentHeating, heatingForm, selectedTargetId, simDate]);

  const openCreateModal = () => {
    setEditingPeriod(null);
    form.setFieldsValue({
      startDate: dayjs(simDate).add(1, 'day'),
      endDate: null,
      internalTempC: undefined,
      internalTempAmplitudeC: undefined,
      internalHumidityPct: undefined,
      ventilationEnabled: false,
      ventilationMaxC: 35,
      allowExternalPrecipitation: false,
      isIsolatedZone: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = useCallback((period: GreenhouseClimatePeriod) => {
    setEditingPeriod(period);
    form.setFieldsValue({
      startDate: dayjs(period.startDate),
      endDate: period.endDate ? dayjs(period.endDate) : null,
      internalTempC: period.internalTempC ?? undefined,
      internalTempAmplitudeC: period.internalTempAmplitudeC ?? undefined,
      internalHumidityPct: period.internalHumidityPct ?? undefined,
      ventilationEnabled: period.ventilationEnabled === true,
      ventilationMaxC: period.ventilationMaxC ?? 35,
      allowExternalPrecipitation: period.allowExternalPrecipitation,
      isIsolatedZone: period.isIsolatedZone,
    });
    setIsModalOpen(true);
  }, [form]);

  const handleSubmit = async () => {
    if (!selectedTargetId) return;
    const values = await form.validateFields();
    const payload = buildClimatePayload(values, selectedTargetId);

    if (payload.internalTempC == null && payload.internalHumidityPct == null) {
      form.setFields([
        { name: 'internalTempC', errors: [gt('errors.tempOrHumidityRequired')] },
        { name: 'internalHumidityPct', errors: [gt('errors.tempOrHumidityRequired')] },
      ]);
      return;
    }

    if (payload.internalTempAmplitudeC != null && payload.internalTempC == null) {
      form.setFields([
        { name: 'internalTempAmplitudeC', errors: [gt('errors.amplitudeNeedsTemp')] },
      ]);
      return;
    }

    try {
      if (editingPeriod) {
        await updatePeriod({
          sessionId,
          periodId: editingPeriod.id,
          structuralObjectId: selectedTargetId,
          payload,
        }).unwrap();
        messageApi.success(gt('period.updated'));
      } else {
        await createPeriod({
          sessionId,
          payload,
        }).unwrap();
        messageApi.success(gt('period.created'));
      }
      setIsModalOpen(false);
      setEditingPeriod(null);
      form.resetFields();
    } catch (error) {
      messageApi.error(extractApiError(error, gt('period.saveFailed')));
    }
  };

  const handleDelete = useCallback(async (period: GreenhouseClimatePeriod) => {
    try {
      await deletePeriod({
        sessionId,
        periodId: period.id,
        structuralObjectId: period.structuralObjectId,
      }).unwrap();
      messageApi.success(gt('period.deleted'));
    } catch (error) {
      messageApi.error(extractApiError(error, gt('period.deleteFailed')));
    }
  }, [deletePeriod, messageApi, sessionId]);

  // Install/removal window of the greenhouse structural object — from which day
  // (and until when) the covering affects the bed. Patches the plot plan.
  const handleActiveWindowChange = useCallback(async (activeFrom: string | null, activeTo: string | null) => {
    if (!selectedTarget || !saveId) {
      messageApi.error(gt('errors.noSaveOrPlot'));
      return;
    }
    try {
      await patchPlotPlan({
        saveId,
        plotId: selectedTarget.plotId,
        fieldId: selectedTarget.fieldId,
        operations: [{
          op: 'updateStructuralObject',
          structuralObjectId: selectedTarget.id,
          activeFrom,
          activeTo,
        }],
      }).unwrap();
      setTargets((prev) => prev.map((t) => (t.id === selectedTarget.id ? { ...t, activeFrom, activeTo } : t)));
      messageApi.success(gt('activeWindow.updated'));
    } catch (error) {
      messageApi.error(extractApiError(error, gt('activeWindow.updateFailed')));
    }
  }, [patchPlotPlan, selectedTarget, saveId, messageApi]);

  const handleHeatingEventSubmit = useCallback(async () => {
    if (!selectedTargetId) return;
    if (!saveId) {
      messageApi.error(gt('errors.noSimSave'));
      return;
    }

    try {
      const values = await heatingForm.validateFields();
      const payload = {
        saveId,
        sessionId,
        date: values.eventDate.format('YYYY-MM-DD'),
        action: values.action,
        structuralObjectId: selectedTargetId,
        properties: values.action === 'heating_on'
          ? (values.mode === 'follow_tech_card'
            ? { mode: 'follow_tech_card' }
            : {
                mode: 'manual',
                minC: values.minC ?? 5,
                maxC: values.maxC ?? 25,
                ventilationEnabled: true,
                ventilationMaxC: values.ventilationMaxC ?? 35,
              })
          : undefined,
      };

      await createSimEvent(payload).unwrap();
      messageApi.success(
        values.action === 'heating_on'
          ? gt('heatingEvent.onAdded')
          : gt('heatingEvent.offAdded'),
      );
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in (error as object)) {
        return;
      }
      messageApi.error(extractApiError(error, gt('heatingEvent.addFailed')));
    }
  }, [createSimEvent, heatingForm, messageApi, saveId, selectedTargetId, sessionId]);

  const handleDeleteHeatingEvent = useCallback(async (event: SimEventLike) => {
    if (!saveId) {
      messageApi.error(gt('errors.noSimSave'));
      return;
    }
    try {
      await deleteSimEvent({
        saveId,
        sessionId,
        eventId: event.id,
        structuralObjectId: getSimEventStructuralObjectId(event) || selectedTargetId,
      }).unwrap();
      messageApi.success(gt('heatingEvent.deleted'));
    } catch (error) {
      messageApi.error(extractApiError(error, gt('heatingEvent.deleteFailed')));
    }
  }, [deleteSimEvent, messageApi, saveId, selectedTargetId, sessionId]);

  const temperatureChartOption = useMemo<any>(() => {
    const points = climateTimeseries?.points || [];
    const outMin = t('greenhouse.charts.outTmin');
    const outMax = t('greenhouse.charts.outTmax');
    const inMin = t('greenhouse.charts.inTmin');
    const inMax = t('greenhouse.charts.inTmax');
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: [outMin, outMax, inMin, inMax],
      },
      xAxis: { type: 'category', data: points.map((point) => point.date) },
      yAxis: { type: 'value', name: '°C', scale: true },
      dataZoom: [{ type: 'inside' }, { type: 'slider', start: 70, end: 100 }],
      series: [
        {
          name: outMin,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.tempMin),
        },
        {
          name: outMax,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.tempMax),
        },
        {
          name: inMin,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.tempMin),
        },
        {
          name: inMax,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.tempMax),
        },
      ],
    };
  }, [climateTimeseries?.points, t]);

  const humidityChartOption = useMemo<any>(() => {
    const points = climateTimeseries?.points || [];
    const outHumidity = t('greenhouse.charts.outHumidity');
    const inHumidity = t('greenhouse.charts.inHumidity');
    const outPrecip = t('greenhouse.charts.outPrecipitation');
    const inPrecip = t('greenhouse.charts.inPrecipitation');
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: [outHumidity, inHumidity, outPrecip, inPrecip],
      },
      xAxis: { type: 'category', data: points.map((point) => point.date) },
      yAxis: [
        { type: 'value', name: '%', min: 0, max: 100 },
        { type: 'value', name: t('greenhouse.units.mm'), min: 0 },
      ],
      dataZoom: [{ type: 'inside' }, { type: 'slider', start: 70, end: 100 }],
      series: [
        {
          name: outHumidity,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.humidityPct),
        },
        {
          name: inHumidity,
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.humidityPct),
        },
        {
          name: outPrecip,
          type: 'bar',
          yAxisIndex: 1,
          data: points.map((point) => point.externalWeather.precipitationMm),
        },
        {
          name: inPrecip,
          type: 'bar',
          yAxisIndex: 1,
          data: points.map((point) => point.effectiveWeather.precipitationMm),
        },
      ],
    };
  }, [climateTimeseries?.points, t]);

  const periodColumns = useMemo(() => [
    {
      title: t('greenhouse.columns.period'),
      key: 'period',
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space direction="vertical" size={2}>
          <Space wrap>
            <Text strong>{period.startDate}</Text>
            <Text type="secondary">→</Text>
            <Text>{period.endDate || t('greenhouse.openEnded')}</Text>
            {climateStatus?.climatePeriod?.id === period.id && <Tag color="green">{t('greenhouse.active')}</Tag>}
          </Space>
          <Text type="secondary">{period.mode}</Text>
        </Space>
      ),
    },
    {
      title: t('greenhouse.columns.params'),
      key: 'params',
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space direction="vertical" size={2}>
          <Text>{t('greenhouse.period.temperature', { value: formatTempBand(period.internalTempC, period.internalTempAmplitudeC) })}</Text>
          <Text>{t('greenhouse.period.humidity', { value: formatOptionalNumber(period.internalHumidityPct, '%') })}</Text>
          <Space wrap size={4}>
            <Tag color={period.allowExternalPrecipitation ? 'blue' : 'default'}>
              {period.allowExternalPrecipitation ? t('greenhouse.period.precipPasses') : t('greenhouse.period.precipBlocked')}
            </Tag>
            <Tag color={period.isIsolatedZone ? 'gold' : 'default'}>
              {period.isIsolatedZone ? t('greenhouse.isolatedZone') : t('greenhouse.period.linkedToOutside')}
            </Tag>
            {period.ventilationEnabled && (
              <Tag color="cyan">{t('greenhouse.period.autoVentWith', { value: formatOptionalNumber(period.ventilationMaxC, '°C') })}</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: t('common:labels.actions'),
      key: 'actions',
      width: 140,
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(period)}>
            {t('common:actions.edit')}
          </Button>
          <Popconfirm
            title={t('greenhouse.period.deleteConfirm')}
            description={t('greenhouse.period.deleteConfirmDescription')}
            okText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => handleDelete(period)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} loading={deletingPeriod}>
              {t('common:actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [climateStatus?.climatePeriod?.id, deletingPeriod, handleDelete, openEditModal, t]);

  const heatingEventColumns = useMemo(() => [
    {
      title: t('common:labels.date'),
      key: 'date',
      width: 130,
      render: (_: unknown, event: SimEventLike) => <Text strong>{String(event.date || '—')}</Text>,
    },
    {
      title: t('greenhouse.columns.event'),
      key: 'action',
      width: 130,
      render: (_: unknown, event: SimEventLike) => {
        const action = getSimEventAction(event) as HeatingEventAction;
        return (
          <Tag color={action === 'heating_on' ? 'orange' : 'default'}>
            {heatingActionLabel(action)}
          </Tag>
        );
      },
    },
    {
      title: t('greenhouse.columns.params'),
      key: 'params',
      render: (_: unknown, event: SimEventLike) => {
        const action = getSimEventAction(event);
        if (action === 'heating_off') {
          return <Text type="secondary">{t('greenhouse.heating.offFromDate')}</Text>;
        }

        const notes = parseHeatingEventNotes(event);
        const mode = typeof notes?.mode === 'string' ? notes.mode : 'manual';
        if (mode === 'follow_tech_card') {
          return <Tag color="gold">{heatingModeLabel('follow_tech_card')}</Tag>;
        }

        return (
          <Space wrap size={4}>
            <Tag>{heatingModeLabel(mode)}</Tag>
            {notes?.minC != null && <Tag>min {formatOptionalNumber(Number(notes.minC), '°C')}</Tag>}
            {notes?.maxC != null && <Tag>max {formatOptionalNumber(Number(notes.maxC), '°C')}</Tag>}
            {notes?.ventilationMaxC != null && <Tag>{t('greenhouse.ventShort', { value: formatOptionalNumber(Number(notes.ventilationMaxC), '°C') })}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('common:labels.actions'),
      key: 'actions',
      width: 140,
      render: (_: unknown, event: SimEventLike) => (isBeforeOpenDay(event.date ? String(event.date) : null, openDay) ? (
        <Tooltip title={t('openDay.pastLocked')}>
          <Button size="small" danger icon={<DeleteOutlined />} disabled>
            {t('common:actions.delete')}
          </Button>
        </Tooltip>
      ) : (
        <Popconfirm
          title={t('greenhouse.heatingEvent.deleteConfirm')}
          description={t('greenhouse.heatingEvent.deleteConfirmDescription')}
          okText={t('common:actions.delete')}
          cancelText={t('common:actions.cancel')}
          onConfirm={() => handleDeleteHeatingEvent(event)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} loading={deletingHeatingEvent}>
            {t('common:actions.delete')}
          </Button>
        </Popconfirm>
      )),
    },
  ], [deletingHeatingEvent, handleDeleteHeatingEvent, openDay, t]);

  return (
    <Card
      title={t('greenhouse.title')}
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        <Space>
          {selectedTarget && (
            <Badge
              status={climateStatus?.climatePeriod ? 'processing' : 'default'}
              text={climateStatus?.climatePeriod ? t('greenhouse.layerActive') : t('greenhouse.layerInactive')}
            />
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            disabled={!selectedTarget}
          >
            {t('greenhouse.newPeriod')}
          </Button>
        </Space>
      }
    >
      {messageContextHolder}

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Text strong>{t('greenhouse.subtype.greenhouse')}</Text>
          <Select
            value={selectedTargetId || undefined}
            onChange={setSelectedTargetId}
            placeholder={discoveringTargets ? t('greenhouse.scanningPlans') : t('greenhouse.selectGreenhouse')}
            loading={loadingFields || discoveringTargets}
            options={targets.map((target) => ({ value: target.id, label: target.label }))}
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
          />
          {selectedTarget && (
            <Space wrap size={6}>
              <Tag>{greenhouseSubtypeLabel(selectedTarget.subtype)}</Tag>
              <Tag color="blue">{selectedTarget.fieldName}</Tag>
              <Tag color="cyan">{selectedTarget.plotName}</Tag>
              <Tag color="default">{t('greenhouse.cells', { count: selectedTarget.cellCount })}</Tag>
            </Space>
          )}
          {selectedTarget && (
            <Space wrap size={8} align="center">
              <Text type="secondary">{t('greenhouse.activeWindow.from')}</Text>
              <DatePicker
                size="small"
                placeholder={t('greenhouse.activeWindow.fromStart')}
                disabled={patchingWindow}
                value={selectedTarget.activeFrom ? dayjs(selectedTarget.activeFrom) : null}
                onChange={(d) => handleActiveWindowChange(d ? d.format('YYYY-MM-DD') : null, selectedTarget.activeTo)}
              />
              <Text type="secondary">{t('greenhouse.activeWindow.to')}</Text>
              <DatePicker
                size="small"
                placeholder={t('greenhouse.activeWindow.indefinite')}
                disabled={patchingWindow}
                value={selectedTarget.activeTo ? dayjs(selectedTarget.activeTo) : null}
                onChange={(d) => handleActiveWindowChange(selectedTarget.activeFrom, d ? d.format('YYYY-MM-DD') : null)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('greenhouse.activeWindow.hint')}
              </Text>
            </Space>
          )}
        </Space>

        {!loadingFields && !discoveringTargets && targets.length === 0 && (
          <Alert
            type="info"
            showIcon
            message={t('greenhouse.noTargets.title')}
            description={t('greenhouse.noTargets.description')}
          />
        )}

        {!selectedTarget ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('greenhouse.selectHint')}
          />
        ) : (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title={t('greenhouse.stats.effectiveTemp')}
                    value={formatTempBand(
                      climateStatus?.effectiveWeather.tempMin ?? null,
                      climateStatus?.effectiveWeather.tempMax != null && climateStatus?.effectiveWeather.tempMin != null
                        ? climateStatus.effectiveWeather.tempMax - climateStatus.effectiveWeather.tempMin
                        : null,
                    )}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title={t('greenhouse.stats.humidityInside')}
                    value={climateStatus?.effectiveWeather.humidityPct ?? null}
                    precision={1}
                    suffix="%"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title={t('greenhouse.stats.precipitationInside')}
                    value={climateStatus?.effectiveWeather.precipitationMm ?? null}
                    precision={2}
                    suffix={t('greenhouse.units.mm')}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title={t('greenhouse.stats.activePlantings')}
                    value={climateStatus?.activePlantings.length ?? 0}
                  />
                </Card>
              </Col>
            </Row>

            <Card size="small" loading={loadingStatus}>
              <Descriptions column={{ xs: 1, sm: 2, lg: 4 }} size="small">
                <Descriptions.Item label={t('greenhouse.status.snapshotDate')}>{climateStatus?.date || simDate}</Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.covering')}>
                  {greenhouseSubtypeLabel(climateStatus?.layers.covering?.coveringSubtype || selectedTarget.subtype)}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.isolation')}>
                  {climateStatus?.layers.greenhouseClimate?.isIsolatedZone || climateStatus?.layers.covering?.isIsolatedZone
                    ? t('common:actions.yes')
                    : t('common:actions.no')}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.heating')}>
                  {climateStatus?.layers.heating ? <Tag color="orange">{t('greenhouse.active')}</Tag> : t('greenhouse.status.none')}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.climatePeriod')}>
                  {climateStatus?.climatePeriod
                    ? `${climateStatus.climatePeriod.startDate} → ${climateStatus.climatePeriod.endDate || t('greenhouse.openEnded')}`
                    : t('greenhouse.status.none')}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.outsideTemp')}>
                  {formatOptionalNumber(climateStatus?.externalWeather.tempMin, '°C')} / {formatOptionalNumber(climateStatus?.externalWeather.tempMax, '°C')}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.insideTemp')}>
                  {formatOptionalNumber(climateStatus?.effectiveWeather.tempMin, '°C')} / {formatOptionalNumber(climateStatus?.effectiveWeather.tempMax, '°C')}
                </Descriptions.Item>
                <Descriptions.Item label={t('greenhouse.status.precipContour')}>
                  {climateStatus?.effectiveWeather.precipitationMm === climateStatus?.externalWeather.precipitationMm
                    ? t('greenhouse.status.followsOutside')
                    : t('greenhouse.status.overriddenInside')}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title={t('greenhouse.heating.title')}
              extra={<Text type="secondary">{t('greenhouse.heating.subtitle')}</Text>}
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('greenhouse.heating.infoTitle')}
                  description={t('greenhouse.heating.infoDescription')}
                />

                <Row gutter={16}>
                  <Col xs={24} xl={10}>
                    <Card size="small" loading={loadingStatus}>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label={t('greenhouse.heating.stateAtSnapshot')}>
                          {currentHeating ? <Tag color="orange">{t('greenhouse.heating.on')}</Tag> : <Tag>{t('greenhouse.heating.off')}</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('greenhouse.heating.mode')}>
                          {currentHeating?.mode ? heatingModeLabel(currentHeating.mode) : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('greenhouse.heating.config')}>
                          {buildHeatingSummary(currentHeating)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col xs={24} xl={14}>
                    <Card size="small" title={t('greenhouse.heatingEvent.add')}>
                      <Form form={heatingForm} layout="vertical">
                        <Row gutter={12}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name="eventDate"
                              label={t('common:labels.date')}
                              rules={[{ required: true, message: t('greenhouse.heatingEvent.dateRequired') }]}
                            >
                              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabledDate={disableBeforeOpenDay(openDay)} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item name="action" label={t('greenhouse.heatingEvent.action')} rules={[{ required: true }]}> 
                              <Select
                                options={[
                                  { value: 'heating_on', label: t('greenhouse.heatingEvent.turnOn') },
                                  { value: 'heating_off', label: t('greenhouse.heatingEvent.turnOff') },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {heatingAction === 'heating_on' && (
                          <>
                            <Form.Item name="mode" label={t('greenhouse.heating.mode')} rules={[{ required: true }]}> 
                              <Select
                                options={[
                                  { value: 'manual', label: t('greenhouse.heatingEvent.manualRange') },
                                  { value: 'follow_tech_card', label: t('greenhouse.heatingEvent.followTechCard') },
                                ]}
                              />
                            </Form.Item>

                            {heatingMode === 'manual' ? (
                              <Row gutter={12}>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="minC" label="Min, °C">
                                    <InputNumber style={{ width: '100%' }} min={-20} max={50} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="maxC" label="Max, °C">
                                    <InputNumber style={{ width: '100%' }} min={-20} max={60} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="ventilationMaxC" label={t('greenhouse.heatingEvent.ventilation')}>
                                    <InputNumber style={{ width: '100%' }} min={0} max={80} />
                                  </Form.Item>
                                </Col>
                              </Row>
                            ) : (
                              <Alert
                                type="warning"
                                showIcon
                                message={t('greenhouse.heatingEvent.techCardTitle')}
                                description={t('greenhouse.heatingEvent.techCardDescription')}
                              />
                            )}
                          </>
                        )}

                        <Button
                          type="primary"
                          onClick={handleHeatingEventSubmit}
                          loading={creatingHeatingEvent}
                          disabled={!selectedTargetId}
                        >
                          {t('greenhouse.heatingEvent.add')}
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                </Row>

                <Table
                  rowKey="id"
                  columns={heatingEventColumns}
                  dataSource={heatingEvents}
                  size="small"
                  pagination={false}
                  loading={loadingHeatingEvents}
                  locale={{ emptyText: t('greenhouse.heatingEvent.empty') }}
                />
              </Space>
            </Card>

            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <Card size="small" title={t('greenhouse.charts.temperatureTitle')} loading={loadingTimeseries}>
                  {!climateTimeseries?.points?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('greenhouse.charts.noSeries')} />
                  ) : (
                    <EChart option={temperatureChartOption} height={260} />
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card size="small" title={t('greenhouse.charts.humidityTitle')} loading={loadingTimeseries}>
                  {!climateTimeseries?.points?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('greenhouse.charts.noSeries')} />
                  ) : (
                    <EChart option={humidityChartOption} height={260} />
                  )}
                </Card>
              </Col>
            </Row>

            <Card
              size="small"
              title={t('greenhouse.periods.title')}
              extra={<Text type="secondary">{loadingPeriods ? t('greenhouse.periods.updating') : t('greenhouse.periods.count', { count: periods.length })}</Text>}
            >
              <Table
                rowKey="id"
                columns={periodColumns}
                dataSource={periods}
                size="small"
                pagination={false}
                loading={loadingPeriods}
                locale={{ emptyText: t('greenhouse.periods.empty') }}
              />
            </Card>
          </>
        )}
      </Space>

      <Modal
        title={editingPeriod ? t('greenhouse.modal.editTitle') : t('greenhouse.modal.createTitle')}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingPeriod(null);
        }}
        onOk={handleSubmit}
        confirmLoading={busyMutation}
        okText={editingPeriod ? t('common:actions.save') : t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="startDate"
            label={t('greenhouse.modal.start')}
            rules={[{ required: true, message: t('greenhouse.modal.startRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="endDate" label={t('greenhouse.modal.end')}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="internalTempC" label={t('greenhouse.modal.internalTemp')}>
                <InputNumber style={{ width: '100%' }} min={-40} max={60} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="internalTempAmplitudeC" label={t('greenhouse.modal.amplitude')}>
                <InputNumber style={{ width: '100%' }} min={0} max={30} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="internalHumidityPct" label={t('greenhouse.modal.humidity')}>
            <InputNumber style={{ width: '100%' }} min={0} max={100} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ventilationEnabled" label={t('greenhouse.modal.autoVent')} valuePropName="checked">
                <Switch checkedChildren={t('common:actions.yes')} unCheckedChildren={t('common:actions.no')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(prev, next) => prev.ventilationEnabled !== next.ventilationEnabled}>
                {({ getFieldValue }) => (
                  <Form.Item name="ventilationMaxC" label={t('greenhouse.modal.threshold')}>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={-40}
                      max={80}
                      disabled={getFieldValue('ventilationEnabled') !== true}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="allowExternalPrecipitation" label={t('greenhouse.modal.allowPrecip')} valuePropName="checked">
            <Switch checkedChildren={t('common:actions.yes')} unCheckedChildren={t('common:actions.no')} />
          </Form.Item>
          <Form.Item name="isIsolatedZone" label={t('greenhouse.isolatedZone')} valuePropName="checked">
            <Switch checkedChildren={t('common:actions.yes')} unCheckedChildren={t('common:actions.no')} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default GreenhouseClimatePanel;
